import React, { useState, useContext } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Table, Badge, InputGroup } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import products from "../../assets/data/gastos.json";

export default function GastosListadoCompletePage() {
    const { t } = useContext(TranslatorContext);
    
    // Estados para filtros
    const [filtros, setFiltros] = useState({
        anio: new Date().getFullYear(),
        categoria: '',
        estado: '',
        fechaInicio: '',
        fechaFin: '',
        fondo: '',
        busqueda: ''
    });

    // Generar años disponibles
    const generarAniosDisponibles = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            years.push(i);
        }
        return years;
    };

    // Categorías para filtro
    const categorias = [
        { value: 'gastos_oficina', label: 'Gastos Oficina' },
        { value: 'edmundo_neira', label: 'Edmundo Neira' },
        { value: 'operativa_terreno', label: 'Operativa/Terreno' },
        { value: 'egresos_operativos', label: 'Egresos Operativos' }
    ];

    // Estados para filtro
    const estados = [
        { value: 'aprobado', label: 'Aprobado' },
        { value: 'pendiente', label: 'Pendiente' },
        { value: 'rechazado', label: 'Rechazado' },
        { value: 'en_revision', label: 'En Revisión' }
    ];

    // Fondos para filtro
    const fondos = [
        { value: 'fondo_emergencia', label: 'Fondo Emergencia' },
        { value: 'fondo_inversion', label: 'Fondo Inversión' },
        { value: 'fondo_capitalizacion', label: 'Fondo Capitalización' },
        { value: 'fondo_capacidad_laboral', label: 'Fondo Capacidad Laboral' }
    ];

    // Manejar cambios en filtros
    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Filtrar gastos (simulación - en producción se haría en el backend)
    const gastosFiltrados = products.tbody.filter(gasto => {
        const cumpleAnio = !filtros.anio || gasto.anio === filtros.anio.toString();
        const cumpleCategoria = !filtros.categoria || gasto.category.toLowerCase().includes(filtros.categoria);
        const cumpleEstado = !filtros.estado || gasto.status.toLowerCase() === filtros.estado;
        const cumpleBusqueda = !filtros.busqueda || 
            gasto.heading.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
            gasto.describe.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
            gasto.responsable.toLowerCase().includes(filtros.busqueda.toLowerCase());
        
        return cumpleAnio && cumpleCategoria && cumpleEstado && cumpleBusqueda;
    });

    // Formatear moneda
    const formatCurrency = (amount) => {
        const numAmount = typeof amount === 'string' ? 
            parseFloat(amount.replace(/[^0-9.-]+/g, "")) : amount;
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(numAmount);
    };

    // Limpiar filtros
    const limpiarFiltros = () => {
        setFiltros({
            anio: new Date().getFullYear(),
            categoria: '',
            estado: '',
            fechaInicio: '',
            fechaFin: '',
            fondo: '',
            busqueda: ''
        });
    };

    // Obtener clase de badge según estado
    const getBadgeVariant = (status) => {
        switch(status.toLowerCase()) {
            case 'aprobado': return 'success';
            case 'pendiente': return 'warning';
            case 'rechazado': return 'danger';
            case 'en revision': return 'info';
            default: return 'secondary';
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Listado Completo de Gastos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Listado Completo</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filtros */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5>Filtros de Búsqueda</h5>
                            <div>
                                <Button variant="outline-secondary" size="sm" onClick={limpiarFiltros} className="me-2">
                                    <i className="material-icons me-1">clear</i>
                                    Limpiar
                                </Button>
                                <Button variant="outline-success" size="sm">
                                    <i className="material-icons me-1">download</i>
                                    Exportar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Año</Form.Label>
                                        <Form.Select
                                            name="anio"
                                            value={filtros.anio}
                                            onChange={handleFiltroChange}
                                        >
                                            {generarAniosDisponibles().map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Búsqueda</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                name="busqueda"
                                                value={filtros.busqueda}
                                                onChange={handleFiltroChange}
                                                placeholder="Buscar gastos..."
                                            />
                                            <InputGroup.Text>
                                                <i className="material-icons">search</i>
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Categoría</Form.Label>
                                        <Form.Select
                                            name="categoria"
                                            value={filtros.categoria}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="">Todas</option>
                                            {categorias.map(cat => (
                                                <option key={cat.value} value={cat.value}>
                                                    {cat.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Estado</Form.Label>
                                        <Form.Select
                                            name="estado"
                                            value={filtros.estado}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="">Todos</option>
                                            {estados.map(estado => (
                                                <option key={estado.value} value={estado.value}>
                                                    {estado.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fondo</Form.Label>
                                        <Form.Select
                                            name="fondo"
                                            value={filtros.fondo}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="">Todos</option>
                                            {fondos.map(fondo => (
                                                <option key={fondo.value} value={fondo.value}>
                                                    {fondo.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Resultados */}
                <Col xl={12}>
                    <Card className="mc-card">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5>Gastos - {filtros.anio} ({gastosFiltrados.length} registros)</h5>
                            <div>
                                <Link to="/gastos/registro">
                                    <Button variant="primary" size="sm">
                                        <i className="material-icons me-1">add</i>
                                        Nuevo Gasto
                                    </Button>
                                </Link>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {gastosFiltrados.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="material-icons text-muted" style={{fontSize: '48px'}}>receipt_long</i>
                                    <h5 className="text-muted mt-3">No se encontraron gastos</h5>
                                    <p className="text-muted">Modifica los filtros o registra un nuevo gasto</p>
                                </div>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            {products.thead.map((header, index) => (
                                                <th key={index}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gastosFiltrados.map((gasto, index) => (
                                            <tr key={index}>
                                                <td><strong>{gasto.id}</strong></td>
                                                <td>
                                                    <Badge bg="info" className="me-1">{gasto.anio}</Badge>
                                                </td>
                                                <td>{gasto.tipo_gasto}</td>
                                                <td>
                                                    <div>
                                                        <strong>{gasto.heading}</strong>
                                                        <br />
                                                        <small className="text-muted">{gasto.category}</small>
                                                    </div>
                                                </td>
                                                <td>{gasto.brand}</td>
                                                <td className="text-end">
                                                    <strong>{formatCurrency(gasto.comuna)}</strong>
                                                </td>
                                                <td>
                                                    <Badge bg="secondary" className="me-1">{gasto.fondo}</Badge>
                                                </td>
                                                <td>{gasto.responsable}</td>
                                                <td>
                                                    <Badge bg={getBadgeVariant(gasto.status)}>
                                                        {gasto.status}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div className="d-flex">
                                                        <Button variant="outline-primary" size="sm" className="me-1">
                                                            <i className="material-icons">visibility</i>
                                                        </Button>
                                                        <Button variant="outline-secondary" size="sm" className="me-1">
                                                            <i className="material-icons">edit</i>
                                                        </Button>
                                                        <Button variant="outline-danger" size="sm">
                                                            <i className="material-icons">delete</i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}
